const BASE_URL = (import.meta.env.VITE_API_URL ?? '') + '/api'

function getToken(): string | null {
  return localStorage.getItem('polywally_token')
}

async function request<T>(
  method: string,
  path: string,
  body?: object | FormData
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as any
    let msg = `HTTP ${res.status}`
    if (typeof err?.error === 'string') {
      msg = err.error
      if (typeof err?.details === 'string') msg += `: ${err.details}`
    } else if (typeof err?.message === 'string') {
      msg = err.message
    } else if (Array.isArray(err?.error?.issues) && err.error.issues.length > 0) {
      msg = err.error.issues.map((i: any) => i.message).join(', ')
    } else if (Array.isArray(err?.issues) && err.issues.length > 0) {
      msg = err.issues.map((i: any) => i.message).join(', ')
    } else if (typeof err?.error === 'object' && err.error !== null) {
      msg = JSON.stringify(err.error)
    }
    // Attach the parsed body so callers can read structured fields (e.g. upgradeReason). Code review P2.
    throw Object.assign(new Error(msg), err && typeof err === 'object' ? err : {}, { status: res.status })
  }

  return res.json()
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const api = {
  auth: {
    signup:  (data: { email: string; password: string; name?: string }) =>
      request<{ token: string; user: any }>('POST', '/auth/signup', data),
    login:   (data: { email: string; password: string }) =>
      request<{ token: string; user: any }>('POST', '/auth/login', data),
    me:      () => request<any>('GET', '/auth/me'),
    forgotPassword: (email: string) =>
      request<any>('POST', '/auth/forgot-password', { email }),
    verifyResetCode: (token: string, code: string) =>
      request<any>('POST', '/auth/verify-reset-code', { token, code }),
    changePasswordRequest: (currentPassword: string) =>
      request<any>('POST', '/auth/change-password-request', { currentPassword }),
    changePasswordConfirm: (token: string, code: string) =>
      request<any>('POST', '/auth/change-password-confirm', { token, code }),
    changePasswordApply: (token: string, newPassword: string) =>
      request<any>('POST', '/auth/change-password-apply', { token, newPassword }),
    resetPassword: (token: string, password: string) =>
      request<any>('POST', '/auth/reset-password', { token, password }),
  },

  // ─── Charts ────────────────────────────────────────────────────────────────
  charts: {
    analyze: (formData: FormData) =>
      request<any>('POST', '/charts/analyze', formData),
    history: (limit = 20) =>
      request<any[]>('GET', `/charts/history?limit=${limit}`),
    get:     (id: string) =>
      request<any>('GET', `/charts/${id}`),
    delete:  (id: string) =>
      request<any>('DELETE', `/charts/${id}`),
    analyzeTicker: (ticker: string, timeframe: string, includeFundamentals?: boolean) =>
      request<any>('POST', '/charts/analyze/ticker', { ticker, timeframe, includeFundamentals }),
  },

  // ─── Trading ───────────────────────────────────────────────────────────────
  trading: {
    account:   (mode = 'paper') => request<any>('GET', `/trading/account?mode=${mode}`),
    clock:     (mode = 'paper') => request<any>('GET', `/trading/clock?mode=${mode}`),
    positions: (mode = 'paper') => request<any>('GET', `/trading/positions?mode=${mode}`),
    orders:    (mode = 'paper', status = 'all') =>
      request<any>('GET', `/trading/orders?mode=${mode}&status=${status}`),
    placeOrder: (order: any, mode = 'paper') =>
      request<any>('POST', `/trading/orders?mode=${mode}`, order),
    cancelOrder: (orderId: string, mode = 'paper') =>
      request<any>('DELETE', `/trading/orders/${orderId}?mode=${mode}`),
    quote:      (symbol: string, mode = 'paper') =>
      request<any>('GET', `/trading/quote/${symbol}?mode=${mode}`),
    bars:       (symbol: string, opts: { timeframe?: string; start?: string }, mode = 'paper') =>
      request<any>('GET', `/trading/bars/${symbol}?mode=${mode}&timeframe=${opts.timeframe ?? '1Day'}&start=${opts.start ?? ''}`),
    portfolio:  (period = '1M', mode = 'paper') =>
      request<any>('GET', `/trading/portfolio/history?mode=${mode}&period=${period}`),
    myTrades:   () => request<any[]>('GET', '/trading/my-trades'),
    syncTrades: (mode = 'paper') => request<any>('POST', `/trading/sync-trades?mode=${mode}`),
  },

  // ─── Market Data (Super only — Alpha Vantage) ──────────────────────────────
  market: {
    usage:     ()               => request<any>('GET', '/market/usage'),
    regime:    ()               => request<any>('GET', '/market/regime'),
    quote:     (symbol: string) => request<any>('GET', `/market/quote/${symbol}`),
    intraday:  (symbol: string, interval = '5min') =>
      request<any>('GET', `/market/intraday/${symbol}?interval=${interval}`),
    overview:  (symbol: string) => request<any>('GET', `/market/overview/${symbol}`),
    dcaHistory: (symbol: string, years: number) =>
      request<{ symbol: string; series: { t: number; c: number }[] }>('GET', `/market/dca-history?symbol=${symbol}&years=${years}`),
    feedback: (message: string, page: string) =>
      request<{ ok: boolean }>('POST', '/market/feedback', { message, page }),
  },

  // ─── News ─────────────────────────────────────────────────────────────────
  news: {
    market: () => request<any[]>('GET', '/news/market'),
    ticker: (symbol: string) => request<any[]>('GET', `/news/ticker/${symbol}`),
  },

  // ─── Options ───────────────────────────────────────────────────────────────
  options: {
    analyze: (formData: FormData) =>
      request<any>('POST', '/options/analyze', formData),
    history: (limit = 20) =>
      request<any[]>('GET', `/options/history?limit=${limit}`),
    detail: (id: string) =>
      request<any>('GET', `/options/detail/${id}`),
    delete: (id: string) =>
      request<any>('DELETE', `/options/${id}`),
  },

  // ─── Settings ──────────────────────────────────────────────────────────────
  settings: {
    saveAlpacaKeys: (data: { key: string; secret: string }) =>
      request<any>('POST', '/settings/alpaca', data),
  },

  // ─── Robo Trader ───────────────────────────────────────────────────────────
  robotrader: {
    getSettings: () =>
      request<any>('GET', '/robotrader/settings'),
    saveSettings: (data: any) =>
      request<any>('POST', '/robotrader/settings', data),
    execute: (surges: any[]) =>
      request<any>('POST', '/robotrader/execute', { surges }),
    deactivate: () =>
      request<any>('POST', '/robotrader/deactivate'),
    status: () =>
      request<any>('GET', '/robotrader/status'),
  },

  // ─── DJT Intelligence ────────────────────────────────────────────────────
  djt: {
    signal: () => request<any>('GET', '/djt/signal'),
    feed:   (limit = 50, since?: number) =>
      request<any>('GET', `/djt/feed?limit=${limit}${since ? `&since=${since}` : ''}`),
    tickers: (since?: number) =>
      request<any>('GET', `/djt/tickers${since ? `?since=${since}` : ''}`),
  },

  // (Billing removed v3.55.0 — private single-user system, no SaaS tiers.)

  // ─── Trade Journal ─────────────────────────────────────────────────────────
  journal: {
    list:   (status?: string) => request<any>('GET', `/journal${status ? `?status=${status}` : ''}`),
    create: (entry: any)      => request<any>('POST', '/journal', entry),
    update: (id: string, data: any) => request<any>('PATCH', `/journal/${id}`, data),
    delete: (id: string)      => request<any>('DELETE', `/journal/${id}`),
  },

  // ─── Watchlist ─────────────────────────────────────────────────────────────
  watchlist: {
    list:         ()                              => request<any>('GET',    '/watchlist'),
    quotes:       ()                              => request<any>('GET',    '/watchlist/quotes'),
    add:          (ticker: string, notes?: string) => request<any>('POST',   '/watchlist', { ticker, notes }),
    remove:       (ticker: string)                => request<any>('DELETE', `/watchlist/${ticker}`),
    alerts:       ()                              => request<any>('GET',    '/watchlist/alerts'),
    addAlert:     (data: any)                     => request<any>('POST',   '/watchlist/alerts', data),
    deleteAlert:  (id: string)                    => request<any>('DELETE', `/watchlist/alerts/${id}`),
  },

  // ─── Scanner ───────────────────────────────────────────────────────────────
  scanner: {
    scan:        (tickers: string[]) => request<any>('POST', '/scanner/scan', { tickers }),
    volumeSurge: (params: { floatMax: 20|50|100|0; minChangePct: number; minSurgeRatio: number; maxSpreadCents: number|null }) => request<any>('POST', '/scanner/volume-surge', params),
    calls:       (maxCost = 800) => request<any>('GET', `/scanner/calls?maxCost=${maxCost}`),
    sharpCalls:  (maxCost = 800) => request<any>('GET', `/scanner/sharp-calls?maxCost=${maxCost}`),
    sharpStats:  () => request<any>('GET', '/scanner/sharp-stats'),
    portfolio:   () => request<any>('GET', '/scanner/portfolio'),
  },

  // ─── Position Guardian ───────────────────────────────────────────────────────
  positions: {
    list:     () => request<any>('GET', '/positions'),
    add:      (p: { ticker: string; strike: number; expiry: string; qty: number; entryPrice: number; optionType?: string; source?: string; account?: string }) => request<any>('POST', '/positions', p),
    remove:   (id: string) => request<any>('DELETE', `/positions/${id}`),
    briefing: () => request<any>('GET', '/positions/briefing'),
    analyze:  (id: string) => request<any>('POST', `/positions/${id}/analyze`),
  },
  // ─── Plaid ─────────────────────────────────────────────────────────────────
  plaid: {
    linkToken:         () => request<any>('POST', '/plaid/link-token'),
    exchange:          (publicToken: string) =>
      request<any>('POST', '/plaid/exchange', { publicToken }),
    accounts:          () => request<any[]>('GET', '/plaid/accounts'),
    syncTransactions:  () => request<any>('POST', '/plaid/sync-transactions'),
    insights:          () => request<any[]>('GET', '/plaid/insights'),
  },

  // ─── Earnings ──────────────────────────────────────────────────────────────
  earnings: {
    ticker:   (t: string)           => request<any>('GET',  `/earnings/${t}`),
    upcoming: (tickers: string[])   => request<any>('POST', '/earnings/upcoming', { tickers }),
  },

  // ─── Macro / Economic Calendar ─────────────────────────────────────────────
  macro: {
    calendar:   () => request<any>('GET', '/macro/calendar'),
    indicators: () => request<any>('GET', '/macro/indicators'),
  },

  // ─── Short Interest ────────────────────────────────────────────────────────
  short: {
    ticker: (t: string)           => request<any>('GET',  `/short/${t}`),
    batch:  (tickers: string[])   => request<any>('POST', '/short/batch', { tickers }),
  },

  // ─── Insider Transactions ──────────────────────────────────────────────────
  insider: {
    ticker: (t: string) => request<any>('GET', `/insider/${t}`),
  },

  // ─── Options Flow ──────────────────────────────────────────────────────────
  flow: {
    ticker: (t: string) => request<any>('GET', `/flow/${t}`),
  },

  // ─── Price Alerts ──────────────────────────────────────────────────────────
  alerts: {
    list:          ()                                                    => request<any>('GET',    '/alerts'),
    create:        (data: { ticker: string; condition: string; targetPrice: number; note?: string }) => request<any>('POST', '/alerts', data),
    delete:        (id: string)                                          => request<any>('DELETE', `/alerts/${id}`),
    toggle:        (id: string)                                          => request<any>('PATCH',  `/alerts/${id}/toggle`),
    notifications: ()                                                    => request<any>('GET',    '/alerts/notifications'),
  },

  // ─── AI Journal Analysis ───────────────────────────────────────────────────
  journalAI: {
    analyze: () => request<any>('POST', '/journal-ai/analyze'),
  },
}
